// FE-13 — modelo de dominio de novedades y sus cambios (ADR-015, backend BE-29).
//
// Tres ideas gobiernan todo lo de abajo, y ninguna es evidente leyendo los
// campos:
//
//  1. **Novedad y nota libre son la MISMA entidad.** ADR-015 §33 las unificó a
//     propósito: la nota es la entrada cuyo tipo es `NOTA`. No hay endpoint de
//     notas ni tipo `Note` en este archivo, y no es un olvido.
//
//  2. **`occurredAt` no es `createdAt`.** El primero es cuándo pasó lo que se
//     relata; el segundo, cuándo alguien lo digitó. Una varada de anteayer
//     relatada hoy pertenece a anteayer — y es `occurredAt` quien decide a qué
//     viaje y a qué día pertenece.
//
//  3. **El viaje no se estampa, se deriva.** Una novedad aparece en el
//     expediente de un viaje porque su `occurredAt` cae dentro de la ventana
//     (ADR-024), no porque alguien la haya asignado. `tripId` existe, es
//     opcional, y significa **intención**: cuando está, manda sobre la ventana.

export interface LogbookEntryType {
  id: string;
  /** Identificador ESTABLE en mayúsculas (`VARADA`, `NOTA`). Lo referencian los reportes y no se corrige. */
  code: string;
  label: string;
  /** Desactivado se retira del formulario, pero las novedades que ya lo usan no cambian. */
  active: boolean;
}

/** El tipo de la novedad libre. Viene en la semilla de todo tenant (ADR-015 §49). */
export const NOTE_TYPE_CODE = 'NOTA';

export interface LogbookEntry {
  id: string;
  entryTypeId: string;
  entryTypeCode: string;
  entryTypeLabel: string;
  /** Cuándo OCURRIÓ lo relatado. El instante de escritura es `createdAt`. */
  occurredAt: string;
  /** El relato. El tipo solo clasifica; esto es lo que alguien leerá después. */
  description: string;
  vehicleId?: string;
  vehiclePlate?: string;
  subUnitId?: string;
  driverId?: string;
  driverName?: string;
  /** Presente solo si alguien DECLARÓ el viaje. Su ausencia no saca la novedad del expediente. */
  tripId?: string;
  /** userId de quien la registró. */
  registeredBy: string;
  /**
   * CUÁNTAS fotos tiene, no cuáles. La lista se pide aparte y es deliberado del
   * contrato: 50 novedades con 4 fotos serían 200 objetos cargados para pintar
   * un clip en el timeline.
   */
  attachmentCount?: number;
  createdAt: string;
  updatedAt?: string;
}

/** Una novedad tiene que colgar de al menos uno: una suelta no se puede auditar. */
export function tieneAsociacion(e: { vehicleId?: string; driverId?: string; tripId?: string }): boolean {
  return Boolean(e.vehicleId || e.driverId || e.tripId);
}

export type LogbookAction = 'REGISTERED' | 'CORRECTED';

/** El "de qué a qué" de un campo corregido. Una asociación retirada deja `to` vacío. */
export interface LogbookChange {
  field: string;
  from: string;
  to: string;
}

/**
 * Un paso de la novedad. Es append-only por privilegio en la base: ni el
 * core puede reescribirla.
 *
 * `recordedAt` es el reloj de pared de la anotación, y NO se confunde con el
 * `occurredAt` de la novedad. El backend les puso nombres distintos justo para
 * que nadie los mezcle — que es el defecto que dejó desordenada la novedad
 * del viaje (issue #94).
 */
export interface LogbookTransition {
  action: LogbookAction;
  changes: LogbookChange[];
  /** userId, o `system`. */
  actor: string;
  /** Nombre legible de quien lo hizo (#111). Opcional: el core puede no resolverlo. */
  actorName?: string;
  /** Obligatoria en una corrección; ausente en el registro inicial. */
  reason?: string;
  recordedAt: string;
}

/**
 * Una foto que una persona adjuntó a la novedad (BE-70).
 *
 * **No es evidencia forense.** La del DVR sale del equipo con su cadena de
 * custodia; esta la sube alguien y vale lo que valga su palabra. El
 * `integrityHash` prueba que el archivo que se descarga es el que se subió, no
 * que lo que muestra sea cierto.
 *
 * **Son varias y no se versionan**, al revés que el recibo de un gasto: un
 * accidente son el guardabarros, la placa del otro carro y el croquis, no dos
 * intentos de fotografiar lo mismo. Y **no se pueden borrar** (ADR-007): la
 * bitácora no borra.
 */
export interface LogbookAttachment {
  id: string;
  contentType: string;
  sizeBytes: number;
  /** SHA-256 en hexadecimal, calculado por el core sobre lo que guardó. */
  integrityHash: string;
  /** userId de quien la subió, que no siempre es quien registró la novedad. */
  uploadedBy: string;
  uploadedAt: string;
}

/** El tope del servidor. Pasarse responde 409, no 400: el archivo está bien, lo que sobra es el sitio. */
export const MAX_ADJUNTOS_NOVEDAD = 10;
